import { ICameraService, CameraInfo } from './interfaces/ICameraService';
import { IEventBus } from './interfaces/IEventBus';

export class CameraService implements ICameraService {
  private availableCameras: MediaDeviceInfo[] = [];
  private selectedCameraId: string | null = null;
  private deviceType: 'mobile' | 'desktop' | 'tablet' = 'desktop';
  private preferredCameraFacing: 'environment' | 'user' | null = null;
  private preferredCamera: 'auto' | 'front' | 'back' = 'auto';

  // Optimization: Static caches for performance
  private static cameraCapabilitiesCache = new Map<string, boolean | string>();
  private static deviceEnumerationCache: {
    devices: MediaDeviceInfo[];
    timestamp: number;
  } | null = null;
  private static readonly CACHE_DURATION = 30000; // 30 seconds
  
  // Stream management for optimization
  private currentStreamPromise?: Promise<MediaStream>;
  private lastStreamConstraints?: MediaTrackConstraints;

  constructor(
    private eventBus: IEventBus,
    preferredCamera: 'auto' | 'front' | 'back' = 'auto'
  ) {
    this.preferredCamera = preferredCamera;
  }

  async detectDeviceType(): Promise<'mobile' | 'desktop' | 'tablet'> {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android/i.test(userAgent) && window.innerWidth >= 768;
    
    if (isTablet) {
      this.deviceType = 'tablet';
    } else if (isMobile) {
      this.deviceType = 'mobile';
    } else {
      this.deviceType = 'desktop';
    }
    
    
    return this.deviceType;
  }

  async enumerateDevices(): Promise<MediaDeviceInfo[]> {
    try {
      // Check cache first for optimization
      const now = Date.now();
      if (CameraService.deviceEnumerationCache &&
          (now - CameraService.deviceEnumerationCache.timestamp) < CameraService.CACHE_DURATION) {
        this.availableCameras = CameraService.deviceEnumerationCache.devices;
        await this.setInitialCameraPreference();
        this.eventBus.emit('camera-changed', this.selectedCameraId);
        return this.availableCameras;
      }

      const permissionStatus = await this.checkCameraPermission();

      if (permissionStatus === 'denied') {
        return [];
      }

      if (permissionStatus === 'prompt') {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach(track => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const allCameras = devices.filter(device => device.kind === 'videoinput');

      // Filter out virtual cameras
      this.availableCameras = allCameras.filter(camera => {
        const isVirtual = this.isVirtualCamera(camera);
        if (isVirtual) {
          console.log(`Virtual camera detected and filtered: ${camera.label}`);
        }
        return !isVirtual;
      });

      // Check if there are physical cameras available
      if (this.availableCameras.length === 0) {
        console.log('No physical cameras available');
        this.eventBus.emit('error', new Error('Cámaras virtuales no están permitidas. Use una cámara física.'));
        return [];
      }

      // Cache the results for optimization
      CameraService.deviceEnumerationCache = {
        devices: this.availableCameras,
        timestamp: now
      };

      await this.setInitialCameraPreference();

      this.eventBus.emit('camera-changed', this.selectedCameraId);

      return this.availableCameras;
    } catch (error) {
      this.handleCameraPermissionError(error);
      return [];
    }
  }

  getAvailableCameras(): MediaDeviceInfo[] {
    return [...this.availableCameras];
  }

  isMultipleCamerasAvailable(): boolean {
    return this.availableCameras.length > 1;
  }

  getSelectedCameraId(): string | null {
    return this.selectedCameraId;
  }

  async setSelectedCamera(cameraId: string): Promise<void> {
    const camera = this.availableCameras.find(cam => cam.deviceId === cameraId);
    if (!camera) {
      throw new Error(`Camera with ID ${cameraId} not found`);
    }
    
    this.selectedCameraId = cameraId;
    this.updatePreferredFacing(camera);
    this.eventBus.emit('camera-changed', cameraId);
  }

  getPreferredFacing(): 'environment' | 'user' | null {
    return this.preferredCameraFacing;
  }

  async setupCamera(constraints?: MediaTrackConstraints): Promise<MediaStream> {
    try {
      const finalConstraints = constraints || await this.getMaxResolution();

      // Optimization: Check if we can reuse existing stream with same constraints
      if (this.currentStreamPromise && this.constraintsEqual(finalConstraints, this.lastStreamConstraints)) {
        return await this.currentStreamPromise;
      }

      // Create new stream promise for lazy loading
      this.currentStreamPromise = navigator.mediaDevices.getUserMedia({
        video: finalConstraints,
        audio: false
      });

      this.lastStreamConstraints = finalConstraints;

      const stream = await this.currentStreamPromise;

      // Validate that active stream is not from a virtual camera
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        const deviceId = settings.deviceId;
        if (deviceId) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const currentDevice = devices.find(device => device.deviceId === deviceId);
          if (currentDevice && this.isVirtualCamera(currentDevice)) {
            console.log(`Virtual camera detected in active stream: ${currentDevice.label}`);
            stream.getTracks().forEach(track => track.stop());
            throw new Error('Cámaras virtuales no están permitidas. Use una cámara física.');
          }
        }
      }

      return stream;
    } catch (error) {

      // Fallback to basic constraints if getMaxResolution fails
      const basicConstraints: MediaTrackConstraints = {
        width: { ideal: 1280 },
        height: { ideal: 720 }
      };

      // Only add facingMode if not on desktop to avoid OverconstrainedError
      if (this.deviceType !== 'desktop' && this.preferredCameraFacing) {
        basicConstraints.facingMode = this.preferredCameraFacing;
      }

      this.currentStreamPromise = navigator.mediaDevices.getUserMedia({
        video: basicConstraints,
        audio: false
      });

      this.lastStreamConstraints = basicConstraints;

      const stream = await this.currentStreamPromise;

      // Validate fallback stream as well
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        const deviceId = settings.deviceId;
        if (deviceId) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const currentDevice = devices.find(device => device.deviceId === deviceId);
          if (currentDevice && this.isVirtualCamera(currentDevice)) {
            console.log(`Virtual camera detected in fallback stream: ${currentDevice.label}`);
            stream.getTracks().forEach(track => track.stop());
            throw new Error('Cámaras virtuales no están permitidas. Use una cámara física.');
          }
        }
      }

      return stream;
    }
  }

  async switchCamera(cameraId: string): Promise<void> {
    const selectedCamera = this.availableCameras.find(cam => cam.deviceId === cameraId);
    if (!selectedCamera) {
      await this.enumerateDevices();
      return;
    }

    // Validate that the target camera is not virtual
    if (this.isVirtualCamera(selectedCamera)) {
      console.log(`Attempted to switch to virtual camera: ${selectedCamera.label}`);
      this.eventBus.emit('error', new Error('No se puede cambiar a cámara virtual'));
      return;
    }

    await this.setSelectedCamera(cameraId);
  }




  isRearCamera(stream: MediaStream): boolean {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return false;
    
    const settings = videoTrack.getSettings();
    return settings.facingMode === 'environment';
  }

  async getCameraInfo(): Promise<{
    availableCameras: CameraInfo[];
    selectedCameraId: string | null;
    deviceType: string;
    isMultipleCamerasAvailable: boolean;
    preferredFacing: 'environment' | 'user' | null;
  }> {
    const availableCamerasWithAutofocus = await Promise.all(
      this.availableCameras.map(async (camera) => {
        const hasAutofocus = await this.checkCameraAutofocus(camera.deviceId);
        return {
          id: camera.deviceId,
          label: camera.label || 'Unknown Camera',
          selected: camera.deviceId === this.selectedCameraId,
          hasAutofocus
        };
      })
    );

    return {
      availableCameras: availableCamerasWithAutofocus,
      selectedCameraId: this.selectedCameraId,
      deviceType: this.deviceType,
      isMultipleCamerasAvailable: this.isMultipleCamerasAvailable(),
      preferredFacing: this.preferredCameraFacing
    };
  }

  private async checkCameraPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      if (!navigator.permissions) {
        return 'prompt';
      }
      
      const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return permission.state;
    } catch (error) {
      return 'prompt';
    }
  }

  private handleCameraPermissionError(error: any): void {
    this.availableCameras = [];
    this.eventBus.emit('error', new Error(`Camera permission error: ${error.message}`));
  }

  private async setInitialCameraPreference(): Promise<void> {
    if (this.availableCameras.length === 0) return;
    
    
    if (this.preferredCamera === 'front') {
      await this.selectFrontCamera();
    } else if (this.preferredCamera === 'back') {
      await this.selectBackCamera();
    } else {
      await this.selectAutoCamera();
    }
    
  }

  private async checkCameraAutofocus(deviceId: string): Promise<boolean> {
    try {
      // Check cache first for optimization
      if (CameraService.cameraCapabilitiesCache.has(deviceId)) {
        const cached = CameraService.cameraCapabilitiesCache.get(deviceId)!;
        return typeof cached === 'boolean' ? cached : false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } }
      });
      
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities() as any;
      
      stream.getTracks().forEach(track => track.stop());
      
      const hasFocusMode = capabilities.focusMode && Array.isArray(capabilities.focusMode) && capabilities.focusMode.length > 0;
      const hasAutofocus = hasFocusMode && (
        capabilities.focusMode.includes('continuous') || 
        capabilities.focusMode.includes('auto')
      );
      
      // Cache the result for future use
      CameraService.cameraCapabilitiesCache.set(deviceId, hasAutofocus);
      
      return hasAutofocus;
    } catch (error) {
      // Cache negative result to avoid repeated failures
      CameraService.cameraCapabilitiesCache.set(deviceId, false);
      return false;
    }
  }

  private async selectFrontCamera(): Promise<void> {
    this.preferredCameraFacing = 'user';
    
    const frontCameras = this.availableCameras.filter(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('front') || 
             label.includes('user') ||
             label.includes('selfie') ||
             label.includes('frontal') ||
             (!label.includes('back') && !label.includes('rear') && !label.includes('environment'));
    });
    
    let selectedCamera = null;
    
    if (frontCameras.length > 0) {
      for (const camera of frontCameras) {
        const hasAutofocus = await this.checkCameraAutofocus(camera.deviceId);
        if (hasAutofocus) {
          selectedCamera = camera;
          break;
        }
      }
      
      if (!selectedCamera) {
        selectedCamera = frontCameras[0];
      }
    }
    
    this.selectedCameraId = selectedCamera ? selectedCamera.deviceId : this.availableCameras[0].deviceId;
  }

  private async selectBackCamera(): Promise<void> {
    this.preferredCameraFacing = 'environment';
    
    const backCameras = this.availableCameras.filter(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('back') || 
             label.includes('rear') ||
             label.includes('environment') ||
             label.includes('trasera') ||
             label.includes('posterior');
    });
    
    let selectedCamera = null;
    
    if (backCameras.length > 0) {
      for (const camera of backCameras) {
        const hasAutofocus = await this.checkCameraAutofocus(camera.deviceId);
        if (hasAutofocus) {
          selectedCamera = camera;
          break;
        }
      }
      
      if (!selectedCamera) {
        selectedCamera = backCameras[0];
      }
    } else if (this.availableCameras.length > 1) {
      const lastCamera = this.availableCameras[this.availableCameras.length - 1];
      const hasAutofocus = await this.checkCameraAutofocus(lastCamera.deviceId);
      selectedCamera = hasAutofocus ? lastCamera : this.availableCameras[this.availableCameras.length - 1];
    }
    
    this.selectedCameraId = selectedCamera ? selectedCamera.deviceId : this.availableCameras[0].deviceId;
  }

  private async selectAutoCamera(): Promise<void> {
    if (this.deviceType === 'mobile' || this.deviceType === 'tablet') {
      await this.selectBackCamera();
    } else {
      // For desktop, prefer front camera (usually built-in webcam)
      await this.selectFrontCamera();
    }
  }

  private updatePreferredFacing(camera: MediaDeviceInfo): void {
    const isRearCamera = camera.label.toLowerCase().includes('back') || 
                        camera.label.toLowerCase().includes('rear') ||
                        camera.label.toLowerCase().includes('environment');
    this.preferredCameraFacing = isRearCamera ? 'environment' : 'user';
  }

  private async getMaxResolution(): Promise<MediaTrackConstraints> {
    try {
      // Ensure device type is detected before determining constraints
      if (!this.deviceType || this.deviceType === 'desktop') {
        await this.detectDeviceType();
      }
      
      const videoConstraints: MediaTrackConstraints = {};
      
      // Priority 1: Always use deviceId if available (ensures specific camera with autofocus)
      if (this.selectedCameraId) {
        videoConstraints.deviceId = { exact: this.selectedCameraId };
      } 
      // Priority 2: Use facingMode as fallback when no specific camera is selected
      else if (this.preferredCameraFacing) {
        const facingConstraint = this.deviceType === 'desktop' 
          ? { ideal: this.preferredCameraFacing }
          : { exact: this.preferredCameraFacing };
        videoConstraints.facingMode = facingConstraint;
      } 
      // Priority 3: Fallback to preferredCamera setting
      else {
        if (this.preferredCamera === 'front') {
          videoConstraints.facingMode = "user";
        } else if (this.preferredCamera === 'back') {
          videoConstraints.facingMode = "environment";  
        } else {
          videoConstraints.facingMode = (this.deviceType === 'mobile' || this.deviceType === 'tablet') ? "environment" : "user";
        }
      }
      
      // Optimization: Use cached capabilities if available
      let capabilities: MediaTrackCapabilities;
      
      if (this.selectedCameraId && CameraService.cameraCapabilitiesCache.has(this.selectedCameraId + '_caps')) {
        capabilities = JSON.parse(CameraService.cameraCapabilitiesCache.get(this.selectedCameraId + '_caps') as string);
      } else {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });
        const videoTrack = tempStream.getVideoTracks()[0];
        capabilities = videoTrack.getCapabilities();
        tempStream.getTracks().forEach(track => track.stop());
        
        // Cache capabilities for future use
        if (this.selectedCameraId) {
          CameraService.cameraCapabilitiesCache.set(this.selectedCameraId + '_caps', JSON.stringify(capabilities));
        }
      }
      
      const constraints: MediaTrackConstraints = { ...videoConstraints };
      
      if (capabilities.width && capabilities.height) {
        const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
        const isMobileDevice = this.deviceType === 'mobile' ||
                               /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Optimize resolution based on device type to prevent "slow motion" effect
        // on devices with high-resolution cameras
        let targetWidth: number;
        let targetHeight: number;

        if (isMobileDevice && !isTablet) {
          // Mobile phones: Use 720p max to ensure smooth detection performance
          targetWidth = Math.min(capabilities.width.max, 1280);
          targetHeight = Math.min(capabilities.height.max, 720);
        } else if (isTablet) {
          // Tablets: Can handle slightly higher resolution
          targetWidth = Math.min(capabilities.width.max, 1280);
          targetHeight = Math.min(capabilities.height.max, 720);
        } else {
          // Desktop: Can handle full HD
          targetWidth = Math.min(capabilities.width.max, 1920);
          targetHeight = Math.min(capabilities.height.max, 1080);
        }

        constraints.width = { ideal: targetWidth };
        constraints.height = { ideal: targetHeight };
      }
      
      // Configure autofocus if camera has the capability
      const capabilitiesAny = capabilities as any;
      if (capabilitiesAny.focusMode && Array.isArray(capabilitiesAny.focusMode)) {
        if (capabilitiesAny.focusMode.includes('continuous')) {
          (constraints as any).focusMode = 'continuous';
        } else if (capabilitiesAny.focusMode.includes('auto')) {
          (constraints as any).focusMode = 'auto';
        }
      }
      
      return constraints;
    } catch (err) {
      // Fallback logic with minimal temporary streams
      if (!this.deviceType || this.deviceType === 'desktop') {
        await this.detectDeviceType();
      }
      
      const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
      const isMobileDevice = this.deviceType === 'mobile' ||
                             /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Optimized fallback constraints for device type
      let fallbackWidth: number;
      let fallbackHeight: number;

      if (isMobileDevice && !isTablet) {
        // Mobile phones: Use 720p for smooth performance
        fallbackWidth = 1280;
        fallbackHeight = 720;
      } else if (isTablet) {
        fallbackWidth = 1280;
        fallbackHeight = 720;
      } else {
        // Desktop
        fallbackWidth = 1920;
        fallbackHeight = 1080;
      }

      const fallbackConstraints: MediaTrackConstraints = {
        width: { ideal: fallbackWidth },
        height: { ideal: fallbackHeight }
      };
      
      if (this.selectedCameraId) {
        fallbackConstraints.deviceId = { exact: this.selectedCameraId };
      } else if (this.preferredCameraFacing) {
        const facingConstraint = this.deviceType === 'desktop' 
          ? { ideal: this.preferredCameraFacing }
          : { exact: this.preferredCameraFacing };
        fallbackConstraints.facingMode = facingConstraint;
      } else {
        if (this.preferredCamera === 'front') {
          fallbackConstraints.facingMode = "user";
        } else if (this.preferredCamera === 'back') {
          fallbackConstraints.facingMode = "environment";  
        } else {
          fallbackConstraints.facingMode = (this.deviceType === 'mobile' || this.deviceType === 'tablet') ? "environment" : "user";
        }
      }
      
      return fallbackConstraints;
    }
  }


  // Optimization helper methods
  private constraintsEqual(a?: MediaTrackConstraints, b?: MediaTrackConstraints): boolean {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // Cleanup method for optimization
  public static clearCaches(): void {
    CameraService.cameraCapabilitiesCache.clear();
    CameraService.deviceEnumerationCache = null;
  }

  // Method to invalidate device cache when needed
  public invalidateDeviceCache(): void {
    CameraService.deviceEnumerationCache = null;
  }

  private isVirtualCamera(device: MediaDeviceInfo): boolean {
    const label = device.label.toLowerCase();
    const virtualCameraIndicators = [
      'obs',
      'virtual',
      'snap camera',
      'manycam',
      'xsplit',
      'camtwist',
      'ecamm',
      'nvidia broadcast',
      'droidcam',
      'iriun',
      'elgato',
      'streamlabs',
      'wirecast',
      'zoom virtual',
      'teams virtual',
      'mmhmm',
      'camo',
      'reincubate camo',
      'webcamoid',
      'splitcam',
      'cheese',
      'guvcview',
      'yawcam',
      'webcam 7',
      'altserver'
    ];

    return virtualCameraIndicators.some(indicator => label.includes(indicator));
  }

}